import React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import './ActionChip.css';

/**
 * ActionChip - Chip seleccionable con acciones embebidas
 *
 * Un componente que funciona como categoría/tab seleccionable y puede contener
 * botones de acción internos (dropdown, regenerate, settings).
 * Usa <div role="button"> para permitir botones internos (evita button-in-button error).
 *
 * @param {Component} icon - Icono principal (lucide-react component)
 * @param {string} label - Texto del chip
 * @param {boolean} isActive - Si está seleccionado
 * @param {boolean} isLoading - Muestra spinner en el icono
 * @param {boolean} disabled - Deshabilita interacción
 * @param {boolean} hasDropdown - Muestra chevron para dropdown
 * @param {boolean} isDropdownOpen - Estado del dropdown (rota el chevron)
 * @param {function} onClick - Handler de click principal
 * @param {ReactNode} children - Botones de acción internos (ActionChip.Action)
 * @param {string} title - Tooltip
 * @param {string} className - Clases adicionales
 */
export default function ActionChip({
  icon: Icon,
  label,
  isActive = false,
  isLoading = false,
  disabled = false,
  hasDropdown = false,
  isDropdownOpen = false,
  onClick,
  children,
  title,
  className = '',
}) {
  const handleClick = (e) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={`xc-action-chip ${isActive ? 'xc-action-chip--active' : ''} ${disabled ? 'xc-action-chip--disabled' : ''} ${isDropdownOpen ? 'xc-action-chip--dropdown-open' : ''} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={title}
    >
      {isLoading && isActive ? (
        <Loader2 size={16} className="xc-action-chip__icon spinner" />
      ) : Icon ? (
        <Icon size={16} className="xc-action-chip__icon" />
      ) : null}

      {label && <span className="xc-action-chip__label">{label}</span>}

      {hasDropdown && (
        <ChevronDown
          size={14}
          className={`xc-action-chip__chevron ${isDropdownOpen ? 'xc-action-chip__chevron--open' : ''}`}
        />
      )}

      {/* Botones de acción internos */}
      {children}
    </div>
  );
}

/**
 * ActionChip.Action - Botón de acción interno (minisquare cyan)
 *
 * @param {Component} icon - Icono (lucide-react component)
 * @param {function} onClick - Handler de click
 * @param {boolean} disabled - Deshabilita el botón
 * @param {boolean} isLoading - Muestra spinner
 * @param {string} title - Tooltip
 */
ActionChip.Action = function ActionChipAction({
  icon: Icon,
  onClick,
  disabled = false,
  isLoading = false,
  title,
}) {
  return (
    <button
      type="button"
      className="xc-action-chip__action"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      disabled={disabled}
      title={title}
    >
      {isLoading ? <Loader2 size={14} className="spinner" /> : Icon && <Icon size={14} />}
    </button>
  );
};
